// src/components/Navigation.js
import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { signOut } from 'firebase/auth';
import { Moon, Sun, User } from 'lucide-react';
import Logo from './Logo';
import { useOperatorClaims } from '../hooks/useOperatorClaims';

function Navigation({ hasUnsavedChanges, darkMode, setDarkMode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isOperator } = useOperatorClaims();
  const [anchorEl, setAnchorEl] = useState(null);
  const [avatarUrl, setAvatarUrl] = useState('');

  useEffect(() => {
    const fetchAvatarUrl = async () => {
      const auth = getAuth();
      const user = auth.currentUser;

      if (user) {
        const userDoc = doc(db, 'users', user.uid);
        const userSnapshot = await getDoc(userDoc);
        if (userSnapshot.exists()) {
          const userData = userSnapshot.data();
          setAvatarUrl(userData.avatarUrl || '');
        }
      }
    };

    fetchAvatarUrl();
  }, []);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/auth');
    } catch (error) {
      console.error('Error logging out:', error);
    } finally {
      handleClose();
    }
  };

  const handleHomeClick = () => {
    if (hasUnsavedChanges && window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
      navigate('/');
    } else if (!hasUnsavedChanges) {
      navigate('/');
    }
  };

  const menuRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setAnchorEl(null);
      }
    };

    if (anchorEl) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [anchorEl]);

  const handleAvatarClick = () => {
    setAnchorEl(anchorEl ? null : 'open');
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  console.log('Current location:', location.pathname);

  return (
    <nav className="bg-gradient-to-r from-indigo-600 to-violet-600 text-white shadow-md shadow-indigo-500/30">
      <div className="flex items-center justify-between px-2 md:px-4 py-2 md:py-3">
        <div onClick={handleHomeClick} className="cursor-pointer">
          <Logo size="small" className="text-white" />
        </div>
        <div className="flex items-center gap-2 md:gap-4">
          <Link
            to="/"
            className="px-2 md:px-3 py-1 md:py-2 rounded hover:bg-white/10 transition-colors text-sm md:text-base"
          >
            Ideas
          </Link>
          <button
            onClick={() => navigate('/tags')}
            disabled={location.pathname === '/tags'}
            className="px-2 md:px-3 py-1 md:py-2 rounded hover:bg-white/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm md:text-base"
          >
            Tags
          </button>
          {isOperator && (
            <Link
              to="/admin/entitlements"
              className="px-2 md:px-3 py-1 md:py-2 rounded text-xs md:text-sm opacity-80 hover:opacity-100 hover:bg-white/10 transition-colors border border-white/20"
            >
              Entitlements
            </Link>
          )}
          <button
            onClick={() => setDarkMode(!darkMode)}
            className="p-1.5 md:p-2 rounded hover:bg-white/10 transition-colors"
            aria-label="Toggle dark mode"
          >
            {darkMode ? <Sun className="w-4 h-4 md:w-5 md:h-5" /> : <Moon className="w-4 h-4 md:w-5 md:h-5" />}
          </button>

          <div className="relative" ref={menuRef}>
            <button
              onClick={handleAvatarClick}
              className="p-1 rounded-full hover:bg-white/10 transition-colors"
              aria-label="User menu"
            >
              {avatarUrl && avatarUrl !== '/placeholder-avatar.jpg' ? (
                <img
                  src={avatarUrl}
                  alt="User avatar"
                  className="w-8 h-8 rounded-full"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : null}
              <div className={`w-8 h-8 rounded-full bg-white/20 flex items-center justify-center ${avatarUrl && avatarUrl !== '/placeholder-avatar.jpg' ? 'hidden' : ''}`}>
                <User className="w-5 h-5" />
              </div>
            </button>
            {anchorEl && (
              <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg py-1 z-50 border border-gray-200 dark:border-gray-700">
                <Link
                  to="/profile"
                  onClick={handleMenuClose}
                  className="block px-4 py-2 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  My Account
                </Link>
                <button
                  onClick={handleLogout}
                  className="block w-full text-left px-4 py-2 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}

export default Navigation;